import analytics from "web/script/analytics/analytics";
import requester from "web/script/modules/requester";
import PropTypes from "prop-types";
import React from "react";
import { connect } from "react-redux";
import { openEmailCaptureOverlay } from "web/redux/ducks/email-capture-overlay";
import { openSignupOverlay } from "web/redux/ducks/signup-overlay";
import { languageIsEnglish } from "web/script/modules/language";
import withReduxProvider from "../../redux-provider";
import SaveForLater from "./save-for-later";
import saveForLaterUtils from "./save-for-later-utils";


function mapDispatchToProps(dispatch) {
    return {
        openSignupOverlay: (options) => dispatch(openSignupOverlay(options)),
        openEmailCaptureOverlay: (options) => dispatch(openEmailCaptureOverlay(options)),
    };
}

const eventLabels = {
    feed: "product_card",
    pdp: "product_page",
    oos: "stock_alert",
};

export class SaveForLaterButton extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            isSaving: false,
            isSaved: false,
            isInitialised: false,
        };

        if (props.initialIsSaved != null) {
            this.state.isSaved = props.initialIsSaved;
            this.state.isInitialised = true;
        }

        this.toggleSaved = this.toggleSaved.bind(this);
        this.triggerSignupOverlay = this.triggerSignupOverlay.bind(this);
    }

    async componentDidMount() {
        if (this.state.isInitialised || !this.props.isLoggedIn) {
            return;
        }

        try {
            let isSaved = await saveForLaterUtils.fetchSaveForLaterPins(this.props.productId);
            this.setState({ isSaved });
        } catch (e) {
            console.error(e);
        }
        this.setState({ isInitialised: true });
    }

    async toggleSaved() {
        if (!this.props.isLoggedIn || this.state.isSaving) {
            return;
        }

        this.setState({ isSaving: true });

        const method = "POST";
        const body = {
            product_id: this.props.productId,
        };

        const eventLabel = eventLabels[this.props.pageSource];

        try {
            let response = await requester("/services/pins/", { method, body });
            if (!response.ok || !(response.status === 204 || response.status === 201)) {
                throw new Error(
                    `Could not save/un-save product with id: ${this.options.productId}`
                );
            }

            if (response.status === 204) {
                // product removed from save-for-later
                analytics.event("save_for_later", "remove_item", eventLabel);
                this.setState({ isSaved: false });
            } else if (response.status === 201) {
                // product added to save-for-later
                analytics.event("save_for_later", "add_item", eventLabel);
                this.setState({ isSaved: true });
            }
        } catch (e) {
            // Still want to log the intent, but can't work out from response if it was an add or remove
            // - get this data from the state instead (this won't be accurate if the page
            // has not been refreshed since last add/remove, but is the best we can do here).
            if (this.state.isSaved) {
                analytics.event("save_for_later", "remove_item", eventLabel);
            } else {
                analytics.event("save_for_later", "add_item", eventLabel);
            }

            console.error(e);
        }

        this.setState({ isSaving: false });
    }

    triggerSignupOverlay() {
        const eventLabel = eventLabels[this.props.pageSource];
        analytics.event("save_for_later", "add_item", eventLabel);
        this.props.openEmailCaptureOverlay({
            productId: this.props.productId,
            captureType: `signup_${this.props.pageSource}_save`, // signup_pdp_save or signup_feed_save
        });
    }

    renderLoggedIn() {
        return (
            <SaveForLater
                className={this.props.className}
                cta={this.props.cta}
                isSaved={this.state.isSaved}
                isSaving={this.state.isSaving}
                onClick={this.toggleSaved}
                type={this.props.type}
            />
        );
    }

    renderLoggedOut() {
        return (
            <SaveForLater
                className={this.props.className}
                cta={this.props.cta}
                isSaved={false}
                onClick={this.triggerSignupOverlay}
                type={this.props.type}
            />
        );
    }

    render() {
        if (!languageIsEnglish()) {
            // Don't render save for later button or signup prompt in i18n MVPs
            return null;
        }
        if (this.props.isLoggedIn) {
            return this.renderLoggedIn();
        } else {
            return this.renderLoggedOut();
        }
    }
}

export default withReduxProvider(
    connect(
        null,
        mapDispatchToProps
    )(SaveForLaterButton)
);

SaveForLaterButton.propTypes = {
    className: PropTypes.string,
    cta: PropTypes.string,
    initialIsSaved: PropTypes.bool,
    isLoggedIn: PropTypes.bool.isRequired,
    openSignupOverlay: PropTypes.func.isRequired,
    openEmailCaptureOverlay: PropTypes.func.isRequired,
    pageSource: PropTypes.oneOf(["pdp", "feed", "oos"]).isRequired,
    productId: PropTypes.string.isRequired,
    type: PropTypes.oneOf(["icon", "small", "normal", "stockAlert"]).isRequired,
};

SaveForLaterButton.defaultProps = {
    type: "normal",
};
